// Battle screen and reports (GDD 08 screen 7, slice subset): the live battle with both sides, the next
// round and retreat; the waiting view (G-36); the report list and one report with rounds, losses, loot.
import type { BattleReport, LiveBattle, RoundStack, ShipCounts } from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Swords } from 'lucide-react';
import { useEffect } from 'react';
import { Link, useOutletContext, useParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import {
  queryKeys,
  useBattle,
  useBattleReport,
  useBattleReports,
  useBattles,
} from '../api/queries';
import type { GameContext } from '../components/GameLayout';
import { PlayerLink } from '../components/ProfileLinks';
import { Button, ErrorNotice, Notice, Page, Panel, Spinner } from '../components/ui';
import {
  END_REASON_LABELS,
  formatDuration,
  formatNumber,
  formatShips,
  RESOURCE_KEYS,
  RESOURCE_LABELS,
  SHIP_LABELS,
  SHIP_ORDER,
} from '../labels';
import { useChannelSubscription } from '../state/channels';
import { useNow } from '../state/live';

/** setTimeout cannot wait longer than 2^31 − 1 ms. */
const MAX_TIMEOUT_MS = 2_147_483_647;

function SideCard({
  title,
  side,
  playerId,
  odpLevel,
}: {
  title: string;
  side: LiveBattle['sides']['attacker'];
  playerId: string | undefined;
  odpLevel?: number;
}) {
  const stacks = SHIP_ORDER.filter((t) => side.stacks[t]);
  return (
    <Panel title={title}>
      {stacks.length === 0 ? (
        <p className="text-sm text-muted">No ships left.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {stacks.map((t) => (
            <li key={t} className="flex justify-between gap-2">
              <span className="text-muted">{SHIP_LABELS[t]}</span>
              <span className="font-mono tabular-nums text-text">
                {formatNumber(side.stacks[t]?.count ?? 0)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {odpLevel !== undefined && odpLevel > 0 ? (
        <p className="mt-2 text-xs text-muted">Orbital Defense Platform Lv {odpLevel}</p>
      ) : null}
      <ul className="mt-3 space-y-1 border-t border-line pt-2 text-xs text-muted">
        {side.participants.map((p) => (
          <li key={p.fleetId}>
            {p.playerId === playerId ? 'You' : (p.displayName ?? 'Pirates')}
            {p.joinedBeforeRound > 1 ? ` · joined in round ${p.joinedBeforeRound}` : ''}
            {p.retreating && p.leftAfterRound === null ? ' · retreating' : ''}
            {p.leftReason === 'retreated' ? ` · retreated after round ${p.leftAfterRound}` : ''}
            {p.leftReason === 'destroyed' ? ` · destroyed in round ${p.leftAfterRound}` : ''}
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function BattleScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const { battleId = '' } = useParams();
  const battle = useBattle(galaxyId, battleId);
  const queryClient = useQueryClient();
  const now = useNow();
  const view = battle.data;
  const live = view?.view === 'full' ? view : undefined;
  useChannelSubscription(live?.status === 'active' ? `battle:${battleId}` : undefined);

  const retreat = useMutation({
    mutationFn: () => endpoints.retreat(galaxyId, battleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.battles(galaxyId) }),
  });

  // Refetch shortly after the next round is due, in case its live event is missed.
  const nextRound = live?.nextRoundAt ? Date.parse(live.nextRoundAt) : Number.NaN;
  useEffect(() => {
    if (!Number.isFinite(nextRound)) return;
    const wait = Math.min(MAX_TIMEOUT_MS, Math.max(0, nextRound - Date.now()) + 1500);
    const id = setTimeout(
      () => queryClient.invalidateQueries({ queryKey: queryKeys.battles(galaxyId) }),
      wait
    );
    return () => clearTimeout(id);
  }, [nextRound, queryClient, galaxyId]);

  const canRetreat =
    live?.status === 'active' &&
    live.sides.attacker.participants.some(
      (p) => p.playerId === me?.playerId && p.leftAfterRound === null && !p.retreating
    );

  return (
    <Page>
      <Link
        to={`/g/${galaxyId}/battles`}
        className="inline-flex items-center gap-1 text-sm text-info"
      >
        <ArrowLeft className="size-4" aria-hidden /> Battles
      </Link>
      {battle.isPending ? <Spinner /> : null}
      {battle.error ? <ErrorNotice error={battle.error} /> : null}
      {view?.view === 'waiting' ? (
        <Panel title="Waiting to attack">
          <p data-battle-waiting className="text-sm text-text">
            Another battle is being fought at this system. Your fleet is number {view.queuePosition}{' '}
            in line and attacks when it ends.
          </p>
          <p className="mt-1 text-xs text-muted">
            Started {new Date(view.startedAt).toLocaleString()} · {view.roundsRun} rounds so far
          </p>
        </Panel>
      ) : null}
      {live ? (
        <>
          <header className="space-y-1">
            <h1 className="flex items-center gap-2 text-xl font-semibold text-text">
              <Swords className="size-5 text-accent" aria-hidden /> Battle at {live.systemName}
            </h1>
            <p data-battle-status={live.status} className="text-sm text-muted">
              {live.status === 'active'
                ? `Round ${live.roundsRun + 1} in ${live.nextRoundAt ? formatDuration(Date.parse(live.nextRoundAt) - now) : '—'}`
                : `Ended: ${live.status === 'attacker_won' ? 'attackers won' : 'defenders won'} — ${END_REASON_LABELS[live.endReason ?? ''] ?? ''}`}
              {live.waitingFleets > 0 ? ` · ${live.waitingFleets} fleets waiting` : ''}
            </p>
          </header>
          {retreat.error ? <ErrorNotice error={retreat.error} /> : null}
          {canRetreat ? (
            <Button
              variant="secondary"
              disabled={retreat.isPending}
              onClick={() => retreat.mutate()}
            >
              Retreat my fleets
            </Button>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <SideCard title="Attackers" side={live.sides.attacker} playerId={me?.playerId} />
            <SideCard
              title="Defenders"
              side={live.sides.defender}
              playerId={me?.playerId}
              odpLevel={live.sides.defender.odpLevel}
            />
          </div>
          {live.status !== 'active' ? (
            <Link
              to={`/g/${galaxyId}/battle-reports/${live.id}`}
              className="inline-flex min-h-11 items-center hud-cut-sm bg-accent px-4 text-sm font-semibold text-accent-ink"
            >
              Read the battle report
            </Link>
          ) : null}
        </>
      ) : null}
    </Page>
  );
}

export function BattlesScreen() {
  const { galaxyId } = useOutletContext<GameContext>();
  const battles = useBattles(galaxyId);
  const reports = useBattleReports(galaxyId);
  const active = (battles.data?.items ?? []).filter((b) => b.status === 'active');
  return (
    <Page>
      <Link
        to={`/g/${galaxyId}/fleet`}
        className="inline-flex items-center gap-1 text-sm text-info"
      >
        <ArrowLeft className="size-4" aria-hidden /> Fleets
      </Link>
      <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
        Battles
      </h1>
      {active.length > 0 ? (
        <Panel title="Running now">
          <ul className="space-y-2">
            {active.map((b) => (
              <li key={b.id}>
                <Link
                  to={`/g/${galaxyId}/battles/${b.id}`}
                  data-battle-item={b.id}
                  className="block hud-cut-sm border border-accent/50 bg-accent/10 p-3 text-sm text-accent"
                >
                  {b.systemName} ·{' '}
                  {b.role === 'waiting'
                    ? 'waiting to attack'
                    : `you ${b.role === 'attacker' ? 'attack' : 'defend'}`}{' '}
                  · round {b.roundsRun + 1}
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
      <Panel title="Reports">
        {reports.isPending ? <Spinner /> : null}
        {reports.error ? <ErrorNotice error={reports.error} /> : null}
        {reports.data?.items.length === 0 ? <Notice>No battles yet.</Notice> : null}
        <ul className="space-y-2">
          {reports.data?.items.map((r) => {
            const won = (r.role === 'attacker') === (r.outcome === 'attacker_won');
            return (
              <li key={r.battleId}>
                <Link
                  to={`/g/${galaxyId}/battle-reports/${r.battleId}`}
                  data-report-item={r.battleId}
                  className={`block hud-cut-sm border p-3 text-sm ${
                    r.viewed
                      ? 'border-line bg-panel text-muted'
                      : 'border-info/50 bg-info/10 text-text'
                  }`}
                >
                  <span className={won ? 'text-success' : 'text-danger'}>
                    {won ? 'Victory' : 'Defeat'}
                  </span>{' '}
                  · {r.role === 'attacker' ? 'attack on' : 'defence of'} {r.systemName}
                  <span className="block text-xs text-faint">
                    {r.endedAt ? new Date(r.endedAt).toLocaleString() : ''}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </Panel>
    </Page>
  );
}

const lostLine = (stacks: RoundStack[]) => {
  const lost: ShipCounts = {};
  for (const s of stacks) if (s.destroyed > 0) lost[s.shipType] = s.destroyed;
  return Object.keys(lost).length > 0 ? formatShips(lost) : 'nothing';
};

function ReportBody({
  galaxyId,
  report,
  playerId,
}: {
  galaxyId: string;
  report: BattleReport;
  playerId: string | undefined;
}) {
  const mine = report.participants.find((p) => p.playerId === playerId);
  const won = mine ? (mine.side === 'attacker') === (report.outcome === 'attacker_won') : null;
  const loot = report.participants.reduce(
    (sum, p) => ({
      metal: sum.metal + p.loot.metal,
      gas: sum.gas + p.loot.gas,
      supplies: sum.supplies + p.loot.supplies,
    }),
    { metal: 0, gas: 0, supplies: 0 }
  );
  const looted = loot.metal + loot.gas + loot.supplies > 0;
  return (
    <>
      <header className="space-y-1">
        <h1
          data-report-outcome
          className={`text-xl font-semibold ${won === false ? 'text-danger' : 'text-success'}`}
        >
          {won === null
            ? report.outcome === 'attacker_won'
              ? 'Attackers won'
              : 'Defenders won'
            : won
              ? 'Victory'
              : 'Defeat'}
        </h1>
        <p className="text-sm text-muted">
          <PlayerLink
            galaxyId={galaxyId}
            playerId={report.attacker.playerId}
            name={report.attacker.displayName}
          />{' '}
          attacked {report.system.name}
          {report.defender ? (
            <>
              {' '}
              of{' '}
              <PlayerLink
                galaxyId={galaxyId}
                playerId={report.defender.playerId}
                name={report.defender.displayName}
              />
            </>
          ) : null}{' '}
          — {END_REASON_LABELS[report.endReason]}.
        </p>
        <p className="text-xs text-faint">
          {new Date(report.startedAt).toLocaleString()} · {report.rounds.length}{' '}
          {report.rounds.length === 1 ? 'round' : 'rounds'} · seed {report.seed}
        </p>
      </header>
      <div className="grid gap-4 sm:grid-cols-2">
        <Panel title="Attacking forces">
          <p className="text-sm text-text">{formatShips(report.forces.attacker)}</p>
        </Panel>
        <Panel title="Defending forces">
          <p className="text-sm text-text">{formatShips(report.forces.defender)}</p>
          {report.forces.odpLevel > 0 ? (
            <p className="mt-1 text-xs text-muted">
              Orbital Defense Platform Lv {report.forces.odpLevel}
            </p>
          ) : null}
        </Panel>
      </div>
      {looted ? (
        <Panel title="Loot">
          <p data-report-loot className="text-sm text-accent">
            {RESOURCE_KEYS.map((k) => `${formatNumber(loot[k])} ${RESOURCE_LABELS[k]}`).join(' · ')}
          </p>
        </Panel>
      ) : null}
      <Panel title="Losses">
        <ul className="space-y-2 text-sm">
          {report.participants.map((p) => (
            <li key={p.fleetId} className="hud-cut-sm border border-line bg-void/60 p-3">
              <p className="text-text">
                {p.playerId === playerId ? 'You' : (p.displayName ?? 'Pirates')} ·{' '}
                {p.side === 'attacker' ? 'attacker' : 'defender'}
                {p.joinedBeforeRound > 1 ? ` · joined in round ${p.joinedBeforeRound}` : ''}
              </p>
              <p className="text-xs text-muted">Sent {formatShips(p.shipsAtJoin)}</p>
              <p className="text-xs text-danger">
                Lost {formatShips(p.losses)} ({formatNumber(p.lossValue)} resources)
              </p>
            </li>
          ))}
        </ul>
      </Panel>
      <Panel title="Rounds">
        <ol className="space-y-1 text-xs text-muted">
          {report.rounds.map((r) => (
            <li key={r.round} data-report-round={r.round}>
              Round {r.round}: attackers lost {lostLine(r.stacks.attacker)} · defenders lost{' '}
              {lostLine(r.stacks.defender)}
              {r.joined.length > 0 ? ` · ${r.joined.length} fleets joined` : ''}
            </li>
          ))}
        </ol>
      </Panel>
    </>
  );
}

export function BattleReportScreen() {
  const { galaxyId, me } = useOutletContext<GameContext>();
  const { battleId = '' } = useParams();
  const report = useBattleReport(galaxyId, battleId);
  const queryClient = useQueryClient();
  const loaded = Boolean(report.data);
  useEffect(() => {
    // Opening a report marks it viewed on the server.
    if (loaded)
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports(galaxyId), exact: true });
  }, [loaded, queryClient, galaxyId]);
  return (
    <Page>
      <Link
        to={`/g/${galaxyId}/battles`}
        className="inline-flex items-center gap-1 text-sm text-info"
      >
        <ArrowLeft className="size-4" aria-hidden /> Battles
      </Link>
      {report.isPending ? <Spinner /> : null}
      {report.error ? <ErrorNotice error={report.error} /> : null}
      {report.data ? (
        <ReportBody galaxyId={galaxyId} report={report.data} playerId={me?.playerId} />
      ) : null}
    </Page>
  );
}
